// frontend/src/components/ContentBlockEditor.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react'; // Added useCallback
import { AuthContext } from '../context/AuthContext.jsx';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import logger from '../utils/logger';

function ContentBlockEditor({ chapterId, block, onUpdate, onDelete }) {
  const { token } = useContext(AuthContext);
  // Updated to use blockType instead of type, and handle both old and new schemas
  const blockType = block.blockType || 'text';
  const blockContent = block.content || {};
  const [type, setType] = useState(blockType);
  const [data, setData] = useState(() => {
    let initialData;
    // Handle new schema (block.content)
    if (typeof blockContent === 'object' && blockContent !== null) {
        initialData = JSON.parse(JSON.stringify(blockContent));
    } else if (blockContent !== undefined) {
        initialData = blockContent;
    } else {
        initialData = {};
    }

    // Updated to use blockType
    if (blockType === 'exercise') {
        if (typeof initialData !== 'object' || initialData === null) initialData = {};
        initialData.headings = initialData.headings || ['Heading 1'];
        initialData.rows = initialData.rows && initialData.rows.length > 0 ? initialData.rows : [['']];
        initialData.frozenCells = initialData.frozenCells || {};
        initialData.autoFillCells = initialData.autoFillCells || {};
        initialData.tooltips = initialData.tooltips || {};
        initialData.fixedColumns = initialData.fixedColumns || {};
        initialData.autoFillPrompt = initialData.autoFillPrompt || 'Default prompt: This table is about [topic]. Given the user summary ({{USER_SUMMARY}}) and the current table structure ({{TABLE}}), please fill in the empty cells that are marked for auto-fill.';
        initialData.textContent = initialData.textContent || '';
        initialData.selectedColumn = initialData.selectedColumn !== undefined ? initialData.selectedColumn : null;
        // Remove referenceTableBlockId initialization
    } else if (blockType === 'question' || blockType === 'multi_choice') {
        if (typeof initialData !== 'object' || initialData === null) initialData = {};
        initialData.prompt = initialData.prompt || 'New Question Prompt';
        initialData.questionType = initialData.questionType || 'short_answer';
        initialData.options = initialData.options || [];
    } else if (blockType === 'text' && typeof initialData !== 'string') {
        initialData = initialData.text || '<p></p>';
    }
    return initialData;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState(''); // Add save status for confirmation
  const [uploading, setUploading] = useState(false); // Add uploading state for image uploads
  const [lastFocusedCell, setLastFocusedCell] = useState({ row: 0, col: 0 });

  // Update internal state when the block prop changes
  useEffect(() => {
    const currentBlockType = block.blockType ||  'text';
    const currentContent = block.content || {};
    setType(currentBlockType);
    let newInitialData;

    // Deep copy to avoid mutating the original block prop
    if (typeof currentContent === 'object' && currentContent !== null) {
        newInitialData = JSON.parse(JSON.stringify(currentContent));
    } else if (currentContent !== undefined && currentBlockType !== 'exercise' && currentBlockType !== 'question' && currentBlockType !== 'multi_choice') {
        // For simple types like 'text' or 'image' if content is a primitive
        newInitialData = currentContent;
    } else {
        // Default for undefined content or complex types needing object structure
        newInitialData = {};
    }

    if (currentBlockType === 'exercise') {
        // Ensure newInitialData is an object for table type
        if (typeof newInitialData !== 'object' || newInitialData === null) newInitialData = {};
        newInitialData.headings = newInitialData.headings || ['Heading 1'];
        newInitialData.rows = newInitialData.rows && newInitialData.rows.length > 0 ? newInitialData.rows : [['']];
        newInitialData.frozenCells = newInitialData.frozenCells || {};
        newInitialData.autoFillCells = newInitialData.autoFillCells || {};
        newInitialData.tooltips = newInitialData.tooltips || {};
        newInitialData.fixedColumns = newInitialData.fixedColumns || {};
        newInitialData.autoFillPrompt = newInitialData.autoFillPrompt || 'Default prompt: This table is about [topic]. Given the user summary ({{USER_SUMMARY}}) and the current table structure ({{TABLE}}), please fill in the empty cells that are marked for auto-fill.';
        newInitialData.textContent = newInitialData.textContent || '';
        newInitialData.selectedColumn = newInitialData.selectedColumn !== undefined ? newInitialData.selectedColumn : null;
        // Remove referenceTableBlockId initialization
    } else if (currentBlockType === 'question' || currentBlockType === 'multi_choice') {
        if (typeof newInitialData !== 'object' || newInitialData === null) newInitialData = {};
        newInitialData.prompt = newInitialData.prompt || 'New Question Prompt';
        newInitialData.questionType = newInitialData.questionType || newInitialData.type || 'short_answer';
        newInitialData.options = newInitialData.options || [];
    } else if (currentBlockType === 'ai_chat') {
        if (typeof newInitialData !== 'object' || newInitialData === null) newInitialData = {};
        newInitialData.systemPrompt = newInitialData.systemPrompt || 'You are a helpful assistant.';
        newInitialData.welcomeMessage = newInitialData.welcomeMessage || 'Hello! How can I help you?';
    } else if (currentBlockType === 'text' && typeof newInitialData !== 'string') {
        newInitialData = newInitialData.text || '<p></p>';
    }

    // Only update data if it has actually changed to prevent unnecessary resets
    // Compare stringified versions to detect deep changes
    const currentDataStr = JSON.stringify(data);
    const newDataStr = JSON.stringify(newInitialData);
    if (currentDataStr !== newDataStr) {
      setData(newInitialData);
    }
    
    setError('');
    if (currentBlockType !== 'exercise') {
        setLastFocusedCell({ row: 0, col: 0 });
    }
  }, [block]);

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Use block.index instead of block._id
      const blockIndex = block.index !== undefined ? block.index : 0;
      
      // Ensure content is properly structured based on block type
      let contentToSend = data;
      
      // For text blocks, ensure content is an object with 'text' property
      if (type === 'text' && typeof data === 'string') {
        contentToSend = { text: data };
      }
      // For image blocks, ensure content is an object with 'media' property
      // Handle both string URLs and object format {media: {url: ...}}
      else if (type === 'image') {
        if (typeof data === 'string') {
          contentToSend = { media: { url: data } };
        } else if (data && typeof data === 'object' && data.media) {
          // If it's already an object with media, use it as-is
          contentToSend = { media: typeof data.media === 'string' ? { url: data.media } : data.media };
        } else {
          contentToSend = { media: { url: '' } };
        }
      }
      // For exercise blocks, ensure content is properly structured with all required properties
      else if (type === 'exercise' && data && typeof data === 'object') {
        // Validate and ensure all required properties exist
        contentToSend = {
          headings: Array.isArray(data.headings) ? data.headings : (data.headings ? [data.headings] : ['Heading 1']),
          rows: Array.isArray(data.rows) && data.rows.length > 0 ? data.rows : (data.rows ? [data.rows] : [['']]),
          frozenCells: data.frozenCells || {},
          autoFillCells: data.autoFillCells || {},
          tooltips: data.tooltips || {},
          fixedColumns: data.fixedColumns || {},
          autoFillPrompt: data.autoFillPrompt || '',
          textContent: data.textContent || '',
          selectedColumn: data.selectedColumn !== undefined ? data.selectedColumn : null,
        };
        // Ensure rows array matches headings length
        if (contentToSend.rows.length > 0 && contentToSend.headings.length > 0) {
          contentToSend.rows = contentToSend.rows.map(row => {
            const rowArray = Array.isArray(row) ? row : [row];
            // Pad or trim row to match headings length
            while (rowArray.length < contentToSend.headings.length) {
              rowArray.push('');
            }
            return rowArray.slice(0, contentToSend.headings.length);
          });
        }
      }
      // Backward compatibility: handle 'table' type (should be migrated to 'exercise')
      else if (type === 'table' && data && typeof data === 'object') {
        contentToSend = data; // Table data should already be an object
      }
      // Ensure other types are objects
      else if (typeof data !== 'object' || data === null) {
        contentToSend = {};
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/admin/chapters/${chapterId}/contentblocks/${blockIndex}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          // Updated to use new schema structure
          body: JSON.stringify({ 
            index: blockIndex,
            blockType: type, 
            content: contentToSend,
            metadata: block.metadata || {}
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update content block.');
      }

      const updatedChapter = await response.json();
      // Find block by index instead of _id
      const updatedBlock = updatedChapter.contentBlocks.find(b => b.index === blockIndex);
      onUpdate(updatedBlock);
      
      // Show save confirmation
      setSaveStatus('Saved!');
      setTimeout(() => setSaveStatus(''), 3000); // Clear after 3 seconds
    } catch (err) {
      logger.error('Error in ContentBlockEditor:', err);
      setError(err.message || 'An error occurred while updating the content block.');
      setSaveStatus('');
    } finally {
      setLoading(false);
    }
  };

  // Removed handleDelete - onDelete is called directly from the button
  // Confirmation and error handling are handled in handleDeleteBlock in AdminDashboard

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    switch (newType) {
      case 'text':
        setData({ text: '<p>New Text Content</p>' });
        break;
      case 'image':
        setData({ media: { url: '' } });
        break;
      case 'question':
        setData({
          prompt: 'New Question Prompt',
          questionType: 'short_answer',
          options: [],
        });
        break;
      case 'exercise': // Tables are now exercise blocks
        setData({
          headings: ['Heading 1'],
          rows: [['']],
          frozenCells: {},
          autoFillCells: {},
          tooltips: {},
          fixedColumns: {},
          autoFillPrompt: 'Default prompt: This table is about [topic]. Given the user summary ({{USER_SUMMARY}}) and the current table structure ({{TABLE}}), please fill in the empty cells that are marked for auto-fill.',
          textContent: '',
          selectedColumn: null
        });
        setLastFocusedCell({ row: 0, col: 0 });
        break;
      case 'ai_chat':
        setData({
          systemPrompt: 'You are a helpful assistant who creates interview scripts. Help the user craft interview questions and responses.',
          welcomeMessage: 'Hello! I can help you create an interview script. What type of interview are you working on?',
        });
        break;
      default:
        setData({});
        break;
    }
  };

  const handlePromptChange = (e) => {
    setData(prevData => ({
      ...prevData,
      prompt: e.target.value
    }));
  };

  const handleOptionChange = (index, value) => {
  setData(prevData => ({
    ...prevData,
    options: prevData.options.map((option, idx) => 
      idx === index ? value : option
    )
  }));
};

  const addOption = () => {
    setData(prevData => ({
      ...prevData,
      options: [...prevData.options, ''],
    }));
  };

  const removeOption = (index) => {
    const newOptions = data.options.filter((_, idx) => idx !== index);
    setData(prevData => ({
      ...prevData,
      options: newOptions,
    }));
  };

  const handleHeadingChange = (index, value) => {
    const newHeadings = [...data.headings];
    newHeadings[index] = value;
    setData((prev) => ({ ...prev, headings: newHeadings }));
  };

  const addColumn = () => {
    const newHeadings = [...data.headings, `Heading ${data.headings.length + 1}`];
    const newRows = data.rows.map((row) => [...row, '']);
    setData((prev) => ({ ...prev, headings: newHeadings, rows: newRows }));
  };

  const removeColumn = (colIndexToRemove) => {
    setData(prevData => {
      const newHeadings = prevData.headings.filter((_, i) => i !== colIndexToRemove);
      const newRows = prevData.rows.map((row) => row.filter((_, i) => i !== colIndexToRemove));
      
      const oldFixedColumns = prevData.fixedColumns || {};
      const newFixedColumns = {};
      // Adjust indices for fixed columns
      for (let i = 0; i < prevData.headings.length; i++) {
        if (i === colIndexToRemove) continue;
        
        const isFixed = oldFixedColumns[i];
        const newIndex = i < colIndexToRemove ? i : i - 1;
        
        if (isFixed) {
          newFixedColumns[newIndex] = true;
        }
      }

      // Handle selectedColumn when removing a column
      let newSelectedColumn = prevData.selectedColumn;
      if (newSelectedColumn !== null && newSelectedColumn !== undefined) {
        if (newSelectedColumn === colIndexToRemove) {
          // If removing the selected column, deselect it
          newSelectedColumn = null;
        } else if (newSelectedColumn > colIndexToRemove) {
          // If selected column is after the removed column, shift index down
          newSelectedColumn = newSelectedColumn - 1;
        }
      }

      return { 
        ...prevData, 
        headings: newHeadings, 
        rows: newRows, 
        fixedColumns: newFixedColumns,
        selectedColumn: newSelectedColumn
      };
    });
  };

  const addRow = () => {
    const newRow = new Array(data.headings.length).fill('');
    setData((prev) => ({ ...prev, rows: [...prev.rows, newRow] }));
  };

  const removeRow = (rowIndex) => {
    const newRows = data.rows.filter((_, i) => i !== rowIndex);
    setData((prev) => ({ ...prev, rows: newRows }));
  };

  const moveRowUp = (rowIndex) => {
    if (rowIndex === 0) return; // Can't move first row up
    
    setData(prevData => {
      const newRows = [...prevData.rows];
      // Swap current row with the one above it
      [newRows[rowIndex - 1], newRows[rowIndex]] = [newRows[rowIndex], newRows[rowIndex - 1]];
      
      // Update any cell-specific data (frozen cells, auto-fill cells, tooltips)
      const newFrozenCells = { ...(prevData.frozenCells || {}) };
      const newAutoFillCells = { ...(prevData.autoFillCells || {}) };
      const newTooltips = { ...(prevData.tooltips || {}) };
      
      // For each column, swap the cell metadata between the two rows
      for (let colIndex = 0; colIndex < newRows[rowIndex].length; colIndex++) {
        const currentCellKey = `${rowIndex}-${colIndex}`;
        const aboveCellKey = `${rowIndex - 1}-${colIndex}`;
        
        // Swap frozen cells
        const currentFrozen = newFrozenCells[currentCellKey];
        const aboveFrozen = newFrozenCells[aboveCellKey];
        if (currentFrozen) {
          newFrozenCells[aboveCellKey] = true;
          delete newFrozenCells[currentCellKey];
        } else {
          delete newFrozenCells[aboveCellKey];
        }
        if (aboveFrozen) {
          newFrozenCells[currentCellKey] = true;
        }
        
        // Swap auto-fill cells
        const currentAutoFill = newAutoFillCells[currentCellKey];
        const aboveAutoFill = newAutoFillCells[aboveCellKey];
        if (currentAutoFill) {
          newAutoFillCells[aboveCellKey] = true;
          delete newAutoFillCells[currentCellKey];
        } else {
          delete newAutoFillCells[aboveCellKey];
        }
        if (aboveAutoFill) {
          newAutoFillCells[currentCellKey] = true;
        }
        
        // Swap tooltips
        const currentTooltip = newTooltips[currentCellKey];
        const aboveTooltip = newTooltips[aboveCellKey];
        if (currentTooltip) {
          newTooltips[aboveCellKey] = { ...currentTooltip };
          delete newTooltips[currentCellKey];
        } else {
          delete newTooltips[aboveCellKey];
        }
        if (aboveTooltip) {
          newTooltips[currentCellKey] = { ...aboveTooltip };
        }
      }
      
      return {
        ...prevData,
        rows: newRows,
        frozenCells: newFrozenCells,
        autoFillCells: newAutoFillCells,
        tooltips: newTooltips
      };
    });
  };

  const moveRowDown = (rowIndex) => {
    if (rowIndex === data.rows.length - 1) return; // Can't move last row down
    
    setData(prevData => {
      const newRows = [...prevData.rows];
      // Swap current row with the one below it
      [newRows[rowIndex], newRows[rowIndex + 1]] = [newRows[rowIndex + 1], newRows[rowIndex]];
      
      // Update any cell-specific data (frozen cells, auto-fill cells, tooltips)
      const newFrozenCells = { ...(prevData.frozenCells || {}) };
      const newAutoFillCells = { ...(prevData.autoFillCells || {}) };
      const newTooltips = { ...(prevData.tooltips || {}) };
      
      // For each column, swap the cell metadata between the two rows
      for (let colIndex = 0; colIndex < newRows[rowIndex].length; colIndex++) {
        const currentCellKey = `${rowIndex}-${colIndex}`;
        const belowCellKey = `${rowIndex + 1}-${colIndex}`;
        
        // Swap frozen cells
        const currentFrozen = newFrozenCells[currentCellKey];
        const belowFrozen = newFrozenCells[belowCellKey];
        if (currentFrozen) {
          newFrozenCells[belowCellKey] = true;
          delete newFrozenCells[currentCellKey];
        } else {
          delete newFrozenCells[belowCellKey];
        }
        if (belowFrozen) {
          newFrozenCells[currentCellKey] = true;
        }
        
        // Swap auto-fill cells
        const currentAutoFill = newAutoFillCells[currentCellKey];
        const belowAutoFill = newAutoFillCells[belowCellKey];
        if (currentAutoFill) {
          newAutoFillCells[belowCellKey] = true;
          delete newAutoFillCells[currentCellKey];
        } else {
          delete newAutoFillCells[belowCellKey];
        }
        if (belowAutoFill) {
          newAutoFillCells[currentCellKey] = true;
        }
        
        // Swap tooltips
        const currentTooltip = newTooltips[currentCellKey];
        const belowTooltip = newTooltips[belowCellKey];
        if (currentTooltip) {
          newTooltips[belowCellKey] = { ...currentTooltip };
          delete newTooltips[currentCellKey];
        } else {
          delete newTooltips[belowCellKey];
        }
        if (belowTooltip) {
          newTooltips[currentCellKey] = { ...belowTooltip };
        }
      }
      
      return {
        ...prevData,
        rows: newRows,
        frozenCells: newFrozenCells,
        autoFillCells: newAutoFillCells,
        tooltips: newTooltips
      };
    });
  };

  const handleToggleFixedColumn = (colIndex, isFixed) => {
    setData(prevData => {
      const newFixedColumns = { ...(prevData.fixedColumns || {}) };
      if (isFixed) {
        newFixedColumns[colIndex] = true;
      } else {
        delete newFixedColumns[colIndex];
      }
      return { ...prevData, fixedColumns: newFixedColumns };
    });
  };

  const handleCellChange = (rowIndex, colIndex, value) => {
    const newRows = data.rows.map((row, rIndex) =>
      rIndex === rowIndex
        ? row.map((cell, cIndex) => (cIndex === colIndex ? value : cell))
        : row
    );
    setData((prev) => ({ ...prev, rows: newRows }));
  };

  const toggleFreezeCell = (rowIndex, colIndex) => {
    const key = `${rowIndex}-${colIndex}`;
    const frozenCells = { ...(data.frozenCells || {}) };
    if (frozenCells[key]) {
      delete frozenCells[key];
    } else {
      frozenCells[key] = true;
    }
    setData((prev) => ({ ...prev, frozenCells }));
  };

  const handleTablePaste = useCallback((event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text/plain');
    const pastedDataRows = pastedText.trimEnd().split('\n').map(row => row.split('\t'));

    if (pastedDataRows.length === 0) return;

    const { row: startRow, col: startCol } = lastFocusedCell;

    setData(prevData => {
      // Ensure prevData.rows and prevData.headings are arrays
      const currentRows = Array.isArray(prevData.rows) ? prevData.rows : [];
      const currentHeadings = Array.isArray(prevData.headings) ? prevData.headings : [];
      
      const newRows = JSON.parse(JSON.stringify(currentRows)); // Deep copy
      const newHeadings = [...currentHeadings];
      const frozenCells = prevData.frozenCells || {};

      let maxRowIndexAffected = startRow + pastedDataRows.length - 1;
      
      let maxColIndexPasted = 0;
      pastedDataRows.forEach(row => {
        maxColIndexPasted = Math.max(maxColIndexPasted, row.length -1);
      });
      let maxColIndexAffected = startCol + maxColIndexPasted;


      // Expand headings if needed
      for (let c = newHeadings.length; c <= maxColIndexAffected; c++) {
        newHeadings.push(`Heading ${c + 1}`);
      }

      // Expand rows array if needed (add new rows)
      for (let r = newRows.length; r <= maxRowIndexAffected; r++) {
        newRows.push(Array(newHeadings.length).fill(''));
      }

      // Ensure all rows have enough columns based on newHeadings length
      for (let r = 0; r < newRows.length; r++) {
        // If a row is shorter than newHeadings, pad it
        for (let c = newRows[r].length; c < newHeadings.length; c++) {
          newRows[r].push('');
        }
      }
      
      // Fill in the pasted data
      pastedDataRows.forEach((pastedRow, pRowIdx) => {
        const currentRowIdx = startRow + pRowIdx;
        if (currentRowIdx < newRows.length) { // Check if row exists
          pastedRow.forEach((pastedCell, pColIdx) => {
            const currentColIdx = startCol + pColIdx;
            if (currentColIdx < newHeadings.length) { // Check if column exists within heading bounds
              const cellKey = `${currentRowIdx}-${currentColIdx}`;
              if (!frozenCells[cellKey]) {
                newRows[currentRowIdx][currentColIdx] = pastedCell;
              }
            }
          });
        }
      });

      return { ...prevData, headings: newHeadings, rows: newRows };
    });
  }, [lastFocusedCell]); // Add lastFocusedCell to dependencies

  const renderQuestionFields = () => {
    return (
      <div className="space-y-4">
        {/* Question Type Selector */}
        <div>
          <label className="block font-medium mb-1 dark:text-gray-200">Question Type</label>
          <select
            value={data.questionType || 'short_answer'}
            onChange={(e) => {
              const newType = e.target.value;
              setData((prevData) => {
                // If newType is multiple_choice or checkbox, ensure options array is defined
                if (newType === 'multiple_choice' || newType === 'checkbox') {
                  return {
                    ...prevData,
                    questionType: newType, // Use questionType for new schema
                    options: prevData.options || [''],
                  };
                }
                // For short_answer, you can keep options or empty it if you prefer
                return { 
                  ...prevData, 
                  questionType: newType, // Use questionType for new schema
                  options: [] 
                };
              });
            }}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
          >
            <option value="short_answer">Short Answer</option>
            <option value="multiple_choice">Multiple Choice</option>
            <option value="checkbox">Checkbox</option>
          </select>
        </div>

        {/* Question Prompt */}
        <div>
          <label className="block font-medium mb-1 dark:text-gray-200">Question Prompt</label>
          <textarea
            value={data.prompt}
            onChange={handlePromptChange}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
            rows={3}
          />
        </div>

        {/* Options for Multiple Choice and Checkbox */}
        {((data.questionType) === 'multiple_choice' || (data.questionType) === 'checkbox') && (
          <div>
            <label className="block font-medium mb-1 dark:text-gray-200">Answer Options</label>
            {data.options.map((option, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  className="flex-1 p-2 border rounded mr-2 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
                  placeholder={`Option ${index + 1}`}
                />
                <button
                  onClick={() => removeOption(index)}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                  disabled={data.options.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              onClick={addOption}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              + Add Option
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleToggleColumnAutoFill = (colIndex, isAutoFill) => {
    setData(prevData => {
      const newAutoFillCells = { ...(prevData.autoFillCells || {}) };
      const rows = prevData.rows || [];
      
      // Toggle auto-fill for all cells in this column
      rows.forEach((row, rowIndex) => {
        const cellKey = `${rowIndex}-${colIndex}`;
        const isFrozen = prevData.frozenCells?.[cellKey];
        
        // Only modify if cell is not frozen
        if (!isFrozen) {
          if (isAutoFill) {
            newAutoFillCells[cellKey] = true;
          } else {
            delete newAutoFillCells[cellKey];
          }
        }
      });
      
      return { ...prevData, autoFillCells: newAutoFillCells };
    });
  };

  // Helper function to check if entire column is marked for auto-fill
  const isColumnAutoFill = (colIndex) => {
    const rows = data.rows || [];
    const autoFillCells = data.autoFillCells || {};
    const frozenCells = data.frozenCells || {};
    
    // Check if all non-frozen cells in this column are marked for auto-fill
    let autoFillCount = 0;
    let nonFrozenCount = 0;
    
    rows.forEach((row, rowIndex) => {
      const cellKey = `${rowIndex}-${colIndex}`;
      const isFrozen = frozenCells[cellKey];
      
      if (!isFrozen) {
        nonFrozenCount++;
        if (autoFillCells[cellKey]) {
          autoFillCount++;
        }
      }
    });
    
    return nonFrozenCount > 0 && autoFillCount === nonFrozenCount;
  };

  const renderTableFields = () => {
    const headings = Array.isArray(data.headings) ? data.headings : ['Heading 1'];
    const rows = Array.isArray(data.rows) ? data.rows : [['']];
    const frozenCells = data.frozenCells || {};
    const autoFillCells = data.autoFillCells || {};
    const tooltips = data.tooltips || {};
    const fixedColumns = data.fixedColumns || {};
    const autoFillPromptValue = data.autoFillPrompt || '';
    const textContentValue = data.textContent || '';
    // Remove selectedColumn from admin editor

    return (
      <div>
        {/* Text Content Section */}
        <div className="mb-6">
          <label className="block font-medium mb-2 dark:text-gray-200">Text Content (appears above table)</label>
          <ReactQuill
            theme="snow"
            value={textContentValue}
            onChange={(value) => setData(prev => ({ ...prev, textContent: value }))}
            modules={ContentBlockEditor.modules}
            formats={ContentBlockEditor.formats}
            className="bg-white dark:bg-gray-800 dark:border-gray-600"
            placeholder="Add descriptive text that will appear above the table..."
          />
          <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
            This text will appear above the table in both normal and maximized views.
          </p>
        </div>

        {/* Remove entire Column Selection Section */}

        {/* Headings section */}
        <div className="mb-4">
          <label className="block font-medium mb-1 dark:text-gray-200">Headings</label>
          {headings.map((heading, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={heading}
                onChange={(e) => handleHeadingChange(index, e.target.value)}
                onFocus={() => setLastFocusedCell({ row: -1, col: index })} 
                placeholder={`Column ${index + 1} heading`}
                className="border p-2 mr-2 flex-grow dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
              />
              <label className="flex items-center mr-2 text-xs dark:bg-gray-800 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={fixedColumns[index] || false}
                  onChange={(e) => handleToggleFixedColumn(index, e.target.checked)}
                  className="mr-1"
                />
                Fix
              </label>
              <label className="flex items-center mr-2 text-xs dark:bg-gray-800 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={isColumnAutoFill(index)}
                  onChange={(e) => handleToggleColumnAutoFill(index, e.target.checked)}
                  className="mr-1"
                />
                Auto-Fill Column
              </label>
              <button
                onClick={() => removeColumn(index)}
                className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600 text-sm"
              >
                Remove Col
              </button>
            </div>
          ))}
          <button
            onClick={addColumn}
            className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 text-sm"
          >
            + Add Column
          </button>
        </div>

        {/* Rows section */}
        <div className="mb-4 overflow-x-auto" onPaste={handleTablePaste}>
          <label className="block font-medium mb-1 dark:text-gray-200">Cell Data</label>
          <table className="min-w-full border-collapse">
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, colIndex) => {
                    const cellKey = `${rowIndex}-${colIndex}`;
                    const isFrozen = frozenCells[cellKey];
                    return (
                      <td key={colIndex} className={`border px-2 py-1 ${data.fixedColumns?.[colIndex] ? 'bg-yellow-100' : ''}`}>
                        <div className="flex flex-col space-y-1">
                          <textarea
                            value={cell}
                            disabled={isFrozen}
                            onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                            onFocus={() => setLastFocusedCell({ row: rowIndex, col: colIndex })}
                            placeholder={`R${rowIndex + 1}C${colIndex + 1}`}
                            className={`border p-2 w-full text-sm dark:text-gray-200 dark:border-gray-600 ${isFrozen ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-800'}`}
                            rows={2}
                          />
                          <div className="flex items-center space-x-2 text-xs">
                            <label className={`flex items-center ${isFrozen ? 'opacity-50' : ''} dark:text-gray-200`}>
                              <input
                                type="checkbox"
                                checked={autoFillCells[cellKey] || false}
                                onChange={(e) => {
                                  if (!isFrozen) {
                                    const updated = { ...autoFillCells };
                                    updated[cellKey] = e.target.checked;
                                    setData((prev) => ({ ...prev, autoFillCells: updated }));
                                  }
                                }}
                                disabled={isFrozen}
                                className="mr-1"
                              />
                              Auto-Fill
                            </label>
                            <button
                              onClick={() => {
                                const cellKey = `${rowIndex}-${colIndex}`;
                                const wasCurrentlyFrozen = data.frozenCells?.[cellKey];

                                setData(prevData => {
                                  const newFrozenCells = { ...(prevData.frozenCells || {}) };
                                  if (newFrozenCells[cellKey]) {
                                    delete newFrozenCells[cellKey];
                                  } else {
                                    newFrozenCells[cellKey] = true;
                                  }

                                  let newAutoFillCells = prevData.autoFillCells || {};
                                  let newTooltips = prevData.tooltips || {};

                                  if (!wasCurrentlyFrozen) {
                                    if (newAutoFillCells[cellKey]) {
                                      newAutoFillCells = { ...newAutoFillCells };
                                      delete newAutoFillCells[cellKey];
                                    }
                                  }

                                  return {
                                    ...prevData,
                                    frozenCells: newFrozenCells,
                                    autoFillCells: newAutoFillCells,
                                    tooltips: newTooltips
                                  };
                                });
                              }}
                              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                            >
                              {frozenCells[cellKey] ? 'Unfreeze' : 'Freeze'}
                            </button>
                          </div>
                          {/* Tooltip Input */}
                          <div className="flex items-center space-x-1 text-xs">
                            <label className={`flex items-center dark:text-gray-200`}>
                              <input
                                type="checkbox"
                                checked={tooltips[cellKey]?.enabled || false}
                                onChange={(e) => {
                                    const updatedTooltips = { ...(data.tooltips || {}) };
                                    if (e.target.checked) {
                                      updatedTooltips[cellKey] = { enabled: true, text: tooltips[cellKey]?.text || '' };
                                    } else {
                                      if (updatedTooltips[cellKey]) {
                                        updatedTooltips[cellKey].enabled = false;
                                      }
                                    }
                                    setData((prev) => ({ ...prev, tooltips: updatedTooltips }));
                                }}
                                className="mr-1"
                              />
                              Tooltip
                            </label>
                            {tooltips[cellKey]?.enabled && (
                              <input
                                type="text"
                                value={tooltips[cellKey]?.text || ''}
                                onChange={(e) => {
                                  const updatedTooltips = { ...(data.tooltips || {}) };
                                  if (!updatedTooltips[cellKey]) {
                                    updatedTooltips[cellKey] = { enabled: true, text: '' };
                                  }
                                  updatedTooltips[cellKey] = { ...updatedTooltips[cellKey], text: e.target.value, enabled: true };
                                  setData((prev) => ({ ...prev, tooltips: updatedTooltips }));
                                }}
                                onPaste={(e) => e.stopPropagation()}
                                placeholder="Tooltip text"
                                className="border p-1 text-xs w-full dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                  <td className="border p-1 align-middle">
                    <div className="flex flex-col space-y-1">
                      {/* Move Up Button */}
                      <button
                        onClick={() => moveRowUp(rowIndex)}
                        disabled={rowIndex === 0}
                        className="bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move row up"
                      >
                        ↑
                      </button>
                      
                      {/* Move Down Button */}
                      <button
                        onClick={() => moveRowDown(rowIndex)}
                        disabled={rowIndex === rows.length - 1}
                        className="bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move row down"
                      >
                        ↓
                      </button>
                      
                      {/* Remove Row Button */}
                      <button
                        onClick={() => removeRow(rowIndex)}
                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 text-xs"
                        title="Remove row"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            onClick={addRow}
            className="mt-2 bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600 text-sm"
          >
            + Add Row
          </button>
        </div>

        {/* Auto-fill Prompt Editor */}
        <div className="mt-6 mb-4">
          <label className="block font-medium mb-1 text-sm dark:text-gray-200">Table Auto-fill AI Prompt</label>
          <textarea
            value={autoFillPromptValue}
            onChange={(e) => setData(prev => ({ ...prev, autoFillPrompt: e.target.value }))}
            className="w-full p-2 border rounded text-sm dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
            rows={5}
            placeholder="Enter the AI prompt for auto-filling this table. Use {{USER_SUMMARY}} for the user's AI-generated summary and {{TABLE}} for current table data."
          />
          <p className="text-xs text-gray-500 mt-1">
            Placeholders: <code>{"{{USER_SUMMARY}}"}</code> (user's AI-generated summary), <code>{"{{TABLE}}"}</code> (current table data).
          </p>
        </div>
      </div>
    );
  };

  const renderAIFields = () => {
    return (
      <div>
        <div className="mb-4">
          <label className="block font-medium mb-1 dark:text-gray-200">System Prompt (Instructions for AI)</label>
          <textarea
            value={data.systemPrompt}
            onChange={(e) => setData(prev => ({ ...prev, systemPrompt: e.target.value }))}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
            rows={5}
            placeholder="Instructions to guide the AI's responses..."
          />
        </div>
        <div className="mb-4">
          <label className="block font-medium mb-1 dark:text-gray-200">Welcome Message</label>
          <textarea
            value={data.welcomeMessage}
            onChange={(e) => setData(prev => ({ ...prev, welcomeMessage: e.target.value }))}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
            rows={3}
            placeholder="First message shown to the user..."
          />
        </div>
      </div>
    );
  };

  const renderDataField = () => {
    switch (type) {
      case 'text':
        // Use ReactQuill instead of textarea
        // Handle both string and object formats - normalize to string for ReactQuill
        const textValue = typeof data === 'string' ? data : (data?.text || '<p></p>');
        return (
          <ReactQuill
            theme="snow" // Or "bubble"
            value={textValue}
            onChange={(value) => {
              // ReactQuill passes the HTML content as a string
              // Store as string in state (will be wrapped in object when saving)
              setData(value);
            }}
            modules={ContentBlockEditor.modules} // Optional: configure toolbar
            formats={ContentBlockEditor.formats} // Optional: configure allowed formats
            className="bg-white dark:bg-gray-800 dark:border-gray-600" // Add bg-white if needed for theme contrast
          />
        );
      case 'image': {
        // Handle both string and object formats - normalize to string for input
        const imageValue = typeof data === 'string' ? data : (data?.media?.url || '');
        
        const handleImageUpload = async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          // Validate file type
          const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
          if (!allowedTypes.includes(file.type)) {
            setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
            return;
          }

          // Validate file size (5MB)
          if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
          }

          setUploading(true);
          setError('');

          try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/upload/image`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
              },
              body: formData,
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Failed to upload image');
            }

            const result = await response.json();
            // Update data with the uploaded image URL
            setData(result.url);
            setSaveStatus('Image uploaded!');
            setTimeout(() => setSaveStatus(''), 3000);
          } catch (err) {
            logger.error('Error uploading image:', err);
            setError(err.message || 'Failed to upload image');
          } finally {
            setUploading(false);
          }
        };

        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={imageValue}
                onChange={(e) => {
                  // Store as string in state (will be wrapped in object when saving)
                  setData(e.target.value);
                }}
                placeholder="Image URL or upload file below"
                className="flex-1 p-2 border rounded dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="bg-blue-500 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-600 transition-colors">
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                  disabled={uploading}
                />
              </label>
              {imageValue && (
                <div className="text-sm text-gray-600 dark:text-gray-400 dark:bg-gray-800 dark:border-gray-600">
                  <img src={imageValue.startsWith('http') ? imageValue : `${import.meta.env.VITE_API_URL}${imageValue}`} alt="Preview" className="max-w-xs max-h-32 object-contain border rounded" />
                </div>
              )}
            </div>
          </div>
        );
      }
      case 'question':
        return renderQuestionFields();
      case 'exercise':
        return renderTableFields();
      case 'ai_chat':
        return renderAIFields();
      default:
        return null;
    }
  };

  // Preview Rendering Function
  const renderPreview = () => {
    switch (type) {
      case 'text':
        // Handle both string and object formats for backward compatibility
        const textContent = typeof data === 'string' ? data : (data?.text || '');
        // Use a div with Quill's editor class to properly render all formatting
        return (
          <div 
            className="ql-editor" 
            dangerouslySetInnerHTML={{ __html: textContent }}
            style={{ 
              padding: '12px 15px',
              fontSize: '14px',
              lineHeight: '1.5',
              color: 'inherit'
            }}
          />
        );
      case 'image':
        // Handle both string and object formats for backward compatibility
        const imageUrl = typeof data === 'string' ? data : (data?.media?.url || '');
        return <img src={imageUrl} alt="Preview" className="max-w-full h-auto" />;
      case 'question':
        return (
          <div className="ql-editor">
            <p className="font-semibold" dangerouslySetInnerHTML={{ __html: data.prompt }} />
            {((data.questionType) === 'multiple_choice' || (data.questionType) === 'checkbox') && (
              <ul>
                {data.options.map((option, idx) => (
                  <li key={idx} dangerouslySetInnerHTML={{ __html: option }} />
                ))}
              </ul>
            )}
            {(data.questionType) === 'short_answer' && <p>Short answer question.</p>}
          </div>
        );
      case 'table':
      case 'exercise': // Exercise blocks use the same preview as table blocks
        return (
          <div className="overflow-auto">
            {/* Text Content Preview */}
            {data.textContent && (
              <div 
                className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-200"
                dangerouslySetInnerHTML={{ __html: data.textContent }}
              />
            )}
            
            <table className="min-w-full border text-xs">
              <thead>
                <tr>
                  {(data.headings || []).map((heading, idx) => (
                    <th key={idx} className={`border px-2 py-1 ${data.fixedColumns?.[idx] ? 'bg-yellow-100' : ''}`}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => (
                      <td key={colIndex} className={`border px-2 py-1 ${data.fixedColumns?.[colIndex] ? 'bg-yellow-50' : ''}`}>
                        {cell}{' '}
                        {data.frozenCells && data.frozenCells[`${rowIndex}-${colIndex}`] && (
                          <span className="text-xs text-gray-500">(F)</span>
                        )}
                        {data.tooltips && data.tooltips[`${rowIndex}-${colIndex}`]?.enabled && data.tooltips[`${rowIndex}-${colIndex}`]?.text && (
                          <span className="text-xs text-blue-500 ml-1">(i)</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.autoFillPrompt && (
                <div className="mt-2 p-2 bg-gray-100 border rounded">
                    <p className="text-xs font-semibold text-gray-600">Auto-fill Prompt:</p>
                    <p className="text-xs text-gray-500 whitespace-pre-wrap">{data.autoFillPrompt.length > 100 ? data.autoFillPrompt.substring(0, 97) + "..." : data.autoFillPrompt}</p>
                </div>
            )}
          </div>
        );
      case 'ai_chat':
        return (
          <div>
            <p className="font-semibold mb-2">AI Chat Preview</p>
            <p><strong>System Prompt:</strong> {data.systemPrompt}</p>
            <div className="border p-3 bg-blue-50 rounded mb-2">
              <p className="italic">{data.welcomeMessage}</p>
            </div>
            <p className="text-sm text-gray-500">Users will be able to chat with the AI here...</p>
          </div>
        );
      default:
        return <p>No preview available.</p>;
    }
  };

  return (
    <div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <div className="space-y-4">
        {/* Content Block Type Selector */}
        <div>
          <label className="block font-medium mb-1 dark:text-gray-200">Content Block Type</label>
          <select
            value={type}
            onChange={handleTypeChange}
            className="w-full p-2 border rounded dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
          >
            <option value="text">Text</option>
            <option value="image">Image</option>
            <option value="question">Question</option>
            <option value="exercise">Exercise (Table)</option>
            <option value="ai_chat">AI Chat</option>
          </select>
        </div>

        {/* Data Field */}
        <div>
          <label className="block font-medium mb-1 dark:text-gray-200">Data</label>
          {renderDataField()}
        </div>

        {/* Preview */}
        <div className="mt-6"> {/* Added margin-top for spacing */}
          <h3 className="text-lg font-semibold mb-2 dark:text-gray-200">Preview</h3>
          <div className="p-4 border rounded bg-gray-50 min-h-[100px] dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"> {/* Added min-height */}
            {renderPreview()}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4 mt-4"> {/* Added margin-top */}
          <button
            onClick={handleSave}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => onDelete(block.index !== undefined ? block.index : 0)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition"
          >
            Delete
          </button>
          {saveStatus && (
            <span className="text-green-600 font-medium">{saveStatus}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Optional: Configure Quill modules (toolbar options)
// Simplified for old react-quill version compatibility
ContentBlockEditor.modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }], // Header dropdown with levels
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered'}, { list: 'bullet' }],
    ['blockquote'],
    ['link'],
    ['clean']
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  }
};

// Optional: Configure allowed formats
// Note: 'header' format supports all header levels (1-6) when specified in toolbar
ContentBlockEditor.formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'ordered',
  'blockquote',
  'link',
];

export default ContentBlockEditor;
